import { MenuService } from 'src/app/core/services/menu.service';
import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateChild } from '@angular/router';
import { AuthenticationService } from '../services/authentication-service.service';
import { isNullOrEmpty } from '../utils/common-functions';
import { Menu } from 'server/entity/menu';
import { firstValueFrom, Observable } from 'rxjs';
import { SessionServiceService } from '../services/session-service.service';
import { HttpClient } from '@angular/common/http';
import { UserType } from '../enums/user-type.enum';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {
  menus: any[] = [];
  profile: any;
  constantForRedirectUrl = '/Error/unAuthorize';

  constructor(
    private http: HttpClient,
    private router: Router,
    private authenticationService: AuthenticationService,
    private sessionServiceService: SessionServiceService,
    private menuService: MenuService
  ) {
  }

  async loadPermission() {
    const [apiJson, menus] = await Promise.all([
      this.sessionServiceService.getSession(),
      this.getMenuData().toPromise(),
    ]);

    this.menus = menus || [];

    if (!isNullOrEmpty(apiJson)) {
      this.profile = JSON.parse(apiJson);
    }
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.checkCanActivate(route, state)
  }

  async canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.checkCanActivate(route, state)
  }

  async checkCanActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean>  {
    console.log('checkCanActivate')

    const currentUser = this.authenticationService.currentUserInfo;
    const token = this.authenticationService.currentToken;

    /* 先檢查是否有token，沒有的話導回登入頁 */
    if (isNullOrEmpty(token) || isNullOrEmpty(currentUser?.userName)) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    /* 再檢查token是否過期 */
    const isValidate = await this.authenticationService.validateJwtToken()
    if (!isValidate) {
      this.authenticationService.logout();
      this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }
    if (!this.authenticationService.isEmbedded
      && await this.authenticationService.shouldShowSystemUpgradeDialog(currentUser.userType)) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    if (currentUser.userType == UserType.SuperAdmin.toString()) {
      return true;
    }
    const urlWithoutParam = state.url.split('?')[0];
    /* 如果是首頁或Error Page，因為不在Menu中，直接通過 */
    if (urlWithoutParam == '/') {
      return true;
    }
    if (urlWithoutParam == this.constantForRedirectUrl) {
      return true;
    }
    /* 等待權限資料載入 */
    await this.loadPermission();
    /* 檢查是否有權限，沒有的話導回首頁 */
    try {
      const findMenu = this.menus?.find((menu: Menu) => menu.url == urlWithoutParam);
      if (isNullOrEmpty(findMenu)) {
        this.router.navigate([this.constantForRedirectUrl]);
        return false;
      }

      /* 如果沒有設定權限碼，直接通過 */
      if (isNullOrEmpty(findMenu.permissionCode)) {
        return true;
      }

      const findPermission = this.profile.userProfile.roles
        .map((role: any) => role.permissions)
        .flat()
        .filter((permission: any) => permission.permissionCode == findMenu.permissionCode);

      if (!findPermission.length) {
        this.router.navigate([this.constantForRedirectUrl]);
        return false;
      }
      return true;
    } catch (error) {
      this.router.navigate([this.constantForRedirectUrl]);
      return false;
    }
  }

  checkPermission(permissionCode: string) {
    return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
      const originalMethod = descriptor.value;
      const menus = this.menus;

      descriptor.value = async function (...args: any[]) {
        const hasPermission = menus.some(menu => menu.permissionCode === permissionCode);
        if (!hasPermission) {
          console.error(`Permission denied: ${permissionCode}`);
          return;
        }
        return originalMethod.apply(this, args);
      };

      return descriptor;
    };
  }

  private getMenuData(): Observable<Menu[]> {
    const url = `${environment.authUrl}/menu/getAllMenu`;
    return this.http.get<Menu[]>(url);
  }
}
